/**
 * ExploreBoard — the Explore room's focus + filmstrip (FXC-9009 two-rooms
 * redesign, Phases 2–3: hero inversion + promotion gestures).
 *
 * HERO INVERSION: the response surface IS the Explore hero — one large fused
 * surface (2D heatmap default, 2D|3D flip, evidence segmented control),
 * UNGATED from campaigns: it renders from /composite/surface whenever the
 * project has solved points. A running campaign only ANIMATES the board
 * (points landing, 4s re-poll); it is never its existence condition.
 *
 * FILMSTRIP + GESTURES: live, throttled, low-res mini-cards along the bottom.
 * Slot 0 is the geometry card — LOCKED, unremovable, always live at the
 * selected point (§5.5). CLICK a card → it takes the hero (1-up focus).
 * DRAG a card onto the hero → 2-up split, again → quad (capped — the retired
 * free-form TileBoard must not resurrect). Each lane keeps its own ✕ demote
 * and 2D|3D projection; every lane shares the pins. On touch there is no
 * drag — tap promotes, and splits stack vertically on narrow/portrait (§5.9).
 *
 * Every panel is a stateless Study-Cursor subscriber (useStudyCursor —
 * project+branch keyed): axes, pins, slab, evidence mode, selected point and
 * hover crosshair are shared, so a card cannot desync. Surface fetches key on
 * the DEBOUNCED slice spec (≥150ms trailing) so N panels × 1 slice change
 * coalesce against the single-worker API rather than stampeding it.
 */
import { useMemo, useRef, useState, type CSSProperties, type DragEvent, type ReactNode } from "react";

import type { CampaignDetail } from "../../../api/highliftCampaign";
import { HeatmapPanel, PointOverlay } from "../../adaptive-sampling/primitives";
import { adaptiveSamplingTheme, PLOTLY_PLOT_MARGIN } from "../../adaptive-sampling/primitives/theme";
import {
  useStudyHover,
  type StudyEvidenceMode,
  type StudySelectedPoint,
} from "../../adaptive-sampling/surfaces/useSliceState";
import type { HighliftPalette } from "../foil/useHighliftTheme";
import { PolarPlot, type ClMaxMarker, type PolarSeries } from "../foil/PolarPlot";
import { CampaignPointsPlot } from "./CampaignPointsPlot";
import { FillMap } from "./FillMap";
import { FillTailorSurface } from "./FillTailorSurface";
import { StudyTrustSurface } from "./StudyTrustSurface";
import type { ReplayStep } from "./campaignMath";
import type { SweepLiveValues } from "./campaignMath";
import {
  EXPLORE_CARDS,
  addLane,
  axisLabel,
  boardAxisSpecs,
  evidenceGrid,
  nearestSelectablePoint,
  queueToWhereNext,
  removeLane,
  samplesToBoardPoints,
  stepsToBoardPoints,
  type BoardPoint,
  type EvidenceGrid,
  type ExploreCardId,
  type QueueRowLike,
} from "./exploreBoard";
import { useDebouncedStudySlice, useStudySurface } from "./useStudySurface";

const MONO = "var(--v5-mono, 'JetBrains Mono', Menlo, monospace)";
/** Filmstrip mini plot rect inset (matches .hlw-xb-mini-canvas). */
const MINI_INSET = { t: 4, r: 4, b: 18, l: 4 } as const;
const SURFACE_POLL_MS = 4000;
/** Filmstrip geometry thumb refresh cap (WebKit fps trap — throttled from the
 *  first commit, §7). */
const THUMB_THROTTLE_MS = 250;
/** Pin-slider → store commit throttle: display stays local at pointer rate,
 *  the store (and so the board render + debounced fetch pipeline) sees at
 *  most ~10 patches/s plus a commit on release (adversarial-review fix). */
const PIN_PATCH_THROTTLE_MS = 100;
/** HTML5 drag payload type for strip-card → hero splits. */
const CARD_DRAG_MIME = "text/x-hlw-card";

// ── small utilities ──────────────────────────────────────────────────────────

/** Throttled value: updates at most once per `ms` (leading + trailing). */
function useThrottledValue<T>(value: T, ms: number): T {
  const [out, setOut] = useState(value);
  const lastRef = useRef(0);
  const pendingRef = useRef<number | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  if (value !== out && pendingRef.current == null) {
    const now = Date.now();
    const wait = Math.max(0, lastRef.current + ms - now);
    pendingRef.current = window.setTimeout(() => {
      pendingRef.current = null;
      lastRef.current = Date.now();
      setOut(valueRef.current);
    }, wait);
  }
  return out;
}

/** Map a value into [0,1] over bounds. */
const norm = (v: number, lo: number, hi: number): number =>
  hi === lo ? 0.5 : (v - lo) / (hi - lo);

/** Interpolate the shared sequential palette (existing plasma stops — the same
 *  scale the hero heatmap uses; no new hexes). */
function plasma(t: number): string {
  const p = adaptiveSamplingTheme.sequentialPalette;
  const clamped = Math.max(0, Math.min(1, t));
  return p[Math.round(clamped * (p.length - 1))] ?? p[0]!;
}

// ── slice-drawer pin slider (pointer-rate stays LOCAL; the store sees ≤10/s) ─
// Adversarial-review fix: patching the store per slider tick re-rendered the
// whole board (plotly lanes included) at 60 Hz. The slider now shows its own
// local value at pointer rate, commits to the cursor at most every
// PIN_PATCH_THROTTLE_MS, and always commits the final value on release.

function PinSlider({
  label,
  lo,
  hi,
  value,
  onCommit,
  mono,
  fmt,
}: {
  label: string;
  lo: number;
  hi: number;
  /** The store's current value (adopted while not dragging). */
  value: number;
  onCommit: (v: number) => void;
  mono: CSSProperties;
  fmt?: (v: number) => string;
}) {
  const [local, setLocal] = useState<number | null>(null);
  const lastPatchRef = useRef(0);
  const shown = local ?? value;
  const commitThrottled = (v: number) => {
    setLocal(v);
    const now = Date.now();
    if (now - lastPatchRef.current >= PIN_PATCH_THROTTLE_MS) {
      lastPatchRef.current = now;
      onCommit(v);
    }
  };
  const release = () => {
    if (local != null) onCommit(local);
    setLocal(null);
    lastPatchRef.current = 0;
  };
  return (
    <label className="hlw-xb-drawer-pin">
      <span style={mono}>
        {label} {fmt ? fmt(shown) : shown.toFixed(2)}
      </span>
      <input
        type="range"
        min={lo}
        max={hi}
        step={(hi - lo) / 100 || 0.01}
        value={shown}
        onChange={(e) => commitThrottled(Number(e.target.value))}
        onPointerUp={release}
        onBlur={release}
      />
    </label>
  );
}

// ── crosshair + selected-point layer (pointer-rate; subscribes hover ONLY) ───

function StudyCrosshair({
  projectId,
  branch,
  xBounds,
  yBounds,
  selected,
  margin,
}: {
  projectId: string;
  branch: string;
  xBounds: [number, number] | null;
  yBounds: [number, number] | null;
  /** Selected point position in data coords (already mapped to this view). */
  selected: { x: number; y: number } | null;
  /** Inset to the plot rect (Plotly margins) — zero for edge-to-edge minis. */
  margin?: { t: number; r: number; b: number; l: number };
}) {
  const hover = useStudyHover(projectId, branch);
  if (!xBounds || !yBounds) return null;
  const px = (x: number) => norm(x, xBounds[0], xBounds[1]) * 100;
  const py = (y: number) => 100 - norm(y, yBounds[0], yBounds[1]) * 100;
  const inset = margin ?? { t: 0, r: 0, b: 0, l: 0 };
  const showHover =
    hover &&
    hover.x >= Math.min(...xBounds) && hover.x <= Math.max(...xBounds) &&
    hover.y >= Math.min(...yBounds) && hover.y <= Math.max(...yBounds);
  return (
    <svg
      className="hlw-xb-crosshair"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden
      style={{ top: inset.t, right: inset.r, bottom: inset.b, left: inset.l }}
    >
      {showHover ? (
        <>
          <line x1={0} x2={100} y1={py(hover.y)} y2={py(hover.y)} className="hlw-xb-crossline" />
          <line x1={px(hover.x)} x2={px(hover.x)} y1={0} y2={100} className="hlw-xb-crossline" />
        </>
      ) : null}
      {selected ? (
        <circle cx={px(selected.x)} cy={py(selected.y)} r={1.6} className="hlw-xb-selected" />
      ) : null}
    </svg>
  );
}

// ── mini renders (filmstrip) ─────────────────────────────────────────────────

/** Low-res canvas heatmap for the strip — draws only when the grid changes. */
function MiniHeatmap({ grid }: { grid: EvidenceGrid | null }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawnRef = useRef<EvidenceGrid | null>(null);
  const draw = (canvas: HTMLCanvasElement | null) => {
    canvasRef.current = canvas;
    if (!canvas || !grid || drawnRef.current === grid) return;
    drawnRef.current = grid;
    const nx = grid.x.length;
    const ny = grid.y.length;
    if (!nx || !ny) return;
    canvas.width = nx;
    canvas.height = ny;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let lo = Infinity;
    let hi = -Infinity;
    for (const row of grid.z) for (const v of row) {
      if (!Number.isFinite(v)) continue;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    for (let j = 0; j < ny; j += 1) {
      for (let i = 0; i < nx; i += 1) {
        const v = grid.z[j]?.[i];
        if (!Number.isFinite(v)) continue;
        ctx.fillStyle = plasma(norm(v!, lo, hi));
        // canvas y is flipped vs data y (row 0 = y min = bottom)
        ctx.fillRect(i, ny - 1 - j, 1, 1);
      }
    }
  };
  // Redraw when the grid identity changes (ref callback runs on mount only, so
  // also draw imperatively here).
  if (canvasRef.current && grid && drawnRef.current !== grid) draw(canvasRef.current);
  return <canvas ref={draw} className="hlw-xb-mini-canvas" aria-hidden />;
}

/** Live section silhouette for the locked geometry card — pure SVG from the
 *  deployed contours, throttled so canvas-rate drags cost nothing here. */
export function GeometryThumb({
  configuration,
  palette,
}: {
  configuration: { elements: Array<{ id?: string; label?: string; contour: ReadonlyArray<readonly number[]> }> };
  palette: HighliftPalette;
}) {
  const throttled = useThrottledValue(configuration, THUMB_THROTTLE_MS);
  const paths = useMemo(() => {
    const els = throttled.elements.filter((e) => Array.isArray(e.contour) && e.contour.length >= 3);
    if (!els.length) return null;
    let xLo = Infinity, xHi = -Infinity, yLo = Infinity, yHi = -Infinity;
    for (const el of els) for (const [x, y] of el.contour as number[][]) {
      if (x! < xLo) xLo = x!;
      if (x! > xHi) xHi = x!;
      if (y! < yLo) yLo = y!;
      if (y! > yHi) yHi = y!;
    }
    const w = xHi - xLo || 1;
    const h = yHi - yLo || 1;
    // Letterbox into a 100×44 viewBox, preserving aspect (chord dominates).
    const scale = Math.min(92 / w, 36 / h);
    const ox = (100 - w * scale) / 2;
    const oy = (44 + h * scale) / 2;
    return els.map((el, i) => ({
      key: el.id ?? el.label ?? String(i),
      label: (el.label ?? el.id ?? "").toLowerCase(),
      d: (el.contour as number[][])
        .map(([x, y], k) => `${k === 0 ? "M" : "L"}${(ox + (x! - xLo) * scale).toFixed(1)} ${(oy - (y! - yLo) * scale).toFixed(1)}`)
        .join(" ") + " Z",
    }));
  }, [throttled]);
  if (!paths) return null;
  const stroke = (label: string) =>
    label.includes("flap") || label.includes("flaperon") ? palette.flapEdge
    : label.includes("vane") || label.includes("slat") ? palette.vaneEdge
    : palette.mainEdge;
  return (
    <svg viewBox="0 0 100 44" className="hlw-xb-mini-svg" aria-hidden>
      {paths.map((p) => (
        <path key={p.key} d={p.d} fill="none" stroke={stroke(p.label)} strokeWidth={1} vectorEffect="non-scaling-stroke" />
      ))}
    </svg>
  );
}

/** Mini polar (CL–α) polyline for the strip. */
function MiniPolar({ series, palette }: { series: PolarSeries[]; palette: HighliftPalette }) {
  const first = series[0];
  const d = useMemo(() => {
    if (!first || first.points.length < 2) return null;
    const alphas = first.points.map((p) => p.alpha);
    const cls = first.points.map((p) => p.CL);
    const [aLo, aHi] = [Math.min(...alphas), Math.max(...alphas)];
    const [cLo, cHi] = [Math.min(...cls), Math.max(...cls)];
    return first.points
      .map((p, i) => `${i === 0 ? "M" : "L"}${(norm(p.alpha, aLo, aHi) * 96 + 2).toFixed(1)} ${(40 - norm(p.CL, cLo, cHi) * 36).toFixed(1)}`)
      .join(" ");
  }, [first]);
  if (!d) return null;
  return (
    <svg viewBox="0 0 100 44" className="hlw-xb-mini-svg" aria-hidden>
      <path d={d} fill="none" stroke={palette.primary} strokeWidth={1.2} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/** Map a high-lift element name to its themed CSS stroke colour (mirrors the
 *  workbench's --el-* palette). */
function cpElementColorVar(name: string): string {
  const key = name.toLowerCase();
  if (key.includes("slat")) return "var(--el-slat)";
  if (key.includes("vane")) return "var(--el-vane)";
  if (key.includes("flap") || key.includes("flaperon")) return "var(--el-flap)";
  return "var(--el-main)";
}

export type CpStripData = { elements: Array<{ element: string; upper: string; lower: string }> } | null;

function CpSvg({ cpStrip, mini }: { cpStrip: CpStripData; mini?: boolean }) {
  if (!cpStrip) return null;
  return (
    <svg viewBox="0 0 720 200" preserveAspectRatio="none" className={mini ? "hlw-xb-mini-svg" : undefined} style={mini ? undefined : { position: "absolute", inset: 0, width: "100%", height: "100%" }}>
      {cpStrip.elements.map((el) => {
        const stroke = cpElementColorVar(el.element);
        return (
          <g key={el.element}>
            {el.lower ? <polyline points={el.lower} fill="none" stroke={stroke} strokeWidth={mini ? 2.5 : 1} opacity={0.45} /> : null}
            {el.upper ? <polyline points={el.upper} fill="none" stroke={stroke} strokeWidth={mini ? 4 : 1.8} /> : null}
          </g>
        );
      })}
    </svg>
  );
}

// ── the board ────────────────────────────────────────────────────────────────

export interface ExploreBoardProps {
  projectId: string;
  branch: string;
  persistedProject: boolean;
  palette: HighliftPalette;
  campaign: CampaignDetail | null;
  replaySteps: ReplayStep[];
  live: SweepLiveValues;
  queue: readonly QueueRowLike[];
  polarSeries: PolarSeries[];
  clMax: ClMaxMarker | null;
  stallAlpha: number | null;
  cpStrip: CpStripData;
  configuration: { elements: Array<{ id?: string; label?: string; contour: ReadonlyArray<readonly number[]> }> };
  /** The promoted lanes (1-up / 2-up / quad). lanes[0] is the focus card. */
  lanes: ExploreCardId[];
  onLanesChange: (lanes: ExploreCardId[]) => void;
  /** The live geometry-over-flow hero, when the geometry card is promoted —
   *  the parent renders it HERE instead of the (hidden) Design hero slot so
   *  exactly one canvas instance ever exists. */
  geometryHero?: ReactNode;
  onOpenAdb: () => void;
  /** Extra header chrome (parent-owned; the replay control + trust chips). */
  headerExtra?: ReactNode;
  /** Overlay rendered over the hero lanes (the autoplay caption ticker, §5.7). */
  overlay?: ReactNode;
  /** The "why" terminal — its OWN collapsible strip below the ticker, above the
   *  filmstrip (FXC-9009). The richer sibling of the caption ticker: a running
   *  LLM-narrated log. Additive; parent-owned so autoplay can auto-open it. */
  whyTerminal?: ReactNode;
  /** Bump to pulse the filmstrip (a replay step / campaign batch landing). */
  pulseKey?: number | string | null;
  /** Replay cursor (step index) while a replay narrates — drives the campaign
   *  lane's FillMap; null shows the whole landed set (live campaigns). */
  replayCursor?: number | null;
  /** Resolve a campaign point's run_path → its run node id (FXC-9009 provenance).
   *  The surface owns the run table, so it owns the map; null = not registered
   *  (yet) and the dot simply isn't a door. */
  resolveRunPath?: (runPath: string) => string | null;
}

export function ExploreBoard(props: ExploreBoardProps) {
  const {
    projectId, branch, persistedProject, palette, campaign, replaySteps, live,
    queue, polarSeries, clMax, stallAlpha, cpStrip, configuration, lanes,
    onLanesChange, geometryHero, onOpenAdb, headerExtra, overlay, whyTerminal,
    pulseKey, replayCursor, resolveRunPath,
  } = props;

  // Filmstrip pulse: a landing batch / replay step briefly halos every card —
  // the "watch it learn" beat (§5.7). Transient, CSS-driven, self-clearing.
  const [pulsing, setPulsing] = useState(false);
  const lastPulseRef = useRef<typeof pulseKey>(null);
  if (pulseKey != null && pulseKey !== lastPulseRef.current) {
    lastPulseRef.current = pulseKey;
    if (!pulsing) setPulsing(true);
  }
  const pulseTimerRef = useRef<number | null>(null);
  if (pulsing && pulseTimerRef.current == null) {
    pulseTimerRef.current = window.setTimeout(() => {
      pulseTimerRef.current = null;
      setPulsing(false);
    }, 650);
  }

  // Axis metadata: campaign box when present, else the canonical operating
  // plane — this is what UNGATES the board from campaigns.
  const inputs = useMemo(
    () => boardAxisSpecs(campaign, live),
    // live only feeds FIXED axis values — key on the scalars, not the object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaign, live.flaperon, live.mach, live.reynolds, live.diskLoading],
  );
  // The ONE debounced slice spec + canonical study-surface queries every lane
  // shares (adversarial-review fix: no lane fetches outside this pipeline).
  const { cursor, patchCursor, spec } = useDebouncedStudySlice(projectId, branch, inputs);
  const { xInput, yInput } = cursor;

  // Campaign point → run node (FXC-9009 provenance): clicking a landed dot in
  // the campaign lane selects that point on the shared cursor, carrying the run
  // node id when the run table resolves the point's run_path — the geometry
  // re-rigs, the flow slice follows, and the workbench provenance panel points
  // at the actual run (the polar-click seam, same capability).
  const handlePickStep = useMemo(() => {
    if (!resolveRunPath) return undefined;
    return (step: ReplayStep) => {
      const nodeId = step.runPath ? resolveRunPath(step.runPath) : null;
      patchCursor({
        selectedPoint: {
          runNodeId: nodeId,
          inputs: {
            alpha_deg: step.alphaDeg,
            flap_deploy_deg: step.deployDeg,
            flaperon_deflection_deg: step.flaperonDeg,
            mach: step.mach,
            re_chord_millions: step.reMillions,
            disk_thrust_loading: step.blowing,
          },
        },
      });
    };
  }, [resolveRunPath, patchCursor]);

  const rangedAxes = useMemo(() => inputs.filter((a) => a.mode === "range"), [inputs]);
  const campaignRunning =
    campaign?.state?.status === "running" || campaign?.state?.status === "paused";
  const pollMs = campaignRunning ? SURFACE_POLL_MS : false;

  const clQuery = useStudySurface(projectId, branch, spec, "cl", { enabled: persistedProject, pollMs });
  const cdQuery = useStudySurface(projectId, branch, spec, "cd", { enabled: persistedProject, pollMs });

  const clGrid = useMemo(
    () => (clQuery.data ? evidenceGrid(clQuery.data, cursor.evidenceMode) : null),
    [clQuery.data, cursor.evidenceMode],
  );
  const cdGrid = useMemo(
    () => (cdQuery.data ? evidenceGrid(cdQuery.data, cursor.evidenceMode) : null),
    [cdQuery.data, cursor.evidenceMode],
  );
  const confidenceGrid = useMemo<EvidenceGrid | null>(() => {
    const data = clQuery.data;
    if (!data) return null;
    const base = evidenceGrid(data, "corrected");
    const z = base.z.map((row, j) => row.map((_, i) => data.fused.confidence[j * data.nx + i] ?? NaN));
    return { ...base, z };
  }, [clQuery.data]);

  // Overlay points: campaign steps (animated as they land) else raw samples.
  const boardPoints = useMemo<BoardPoint[]>(
    () =>
      replaySteps.length > 0
        ? stepsToBoardPoints(replaySteps, xInput, yInput)
        : samplesToBoardPoints(clQuery.data),
    [replaySteps, xInput, yInput, clQuery.data],
  );
  const whereNext = useMemo(() => queueToWhereNext(queue, xInput, yInput), [queue, xInput, yInput]);

  // Per-panel 2D|3D flip (local per card — panels share pins, not projection).
  const [viewByCard, setViewByCard] = useState<Partial<Record<ExploreCardId, "2d" | "3d">>>({});

  const selectedXY = useMemo(() => {
    const pt = cursor.selectedPoint;
    if (!pt) return null;
    const x = pt.inputs[xInput];
    const y = pt.inputs[yInput];
    return Number.isFinite(x) && Number.isFinite(y) ? { x: x!, y: y! } : null;
  }, [cursor.selectedPoint, xInput, yInput]);

  // Stable handlers + memoized overlays so a board re-render (throttled pin
  // patches, campaign polls) does not hand plotly fresh props every time
  // (adversarial-review fix: un-memoized HeatmapPanels re-rendered per tick).
  const clickCtxRef = useRef({ steps: replaySteps, surface: clQuery.data, inputs, cursor });
  clickCtxRef.current = { steps: replaySteps, surface: clQuery.data, inputs, cursor };
  const handleHeroClick = useMemo(
    () => (x: number, y: number) => {
      // A click/tap PLACES the mirrored crosshair (§5.9: hover doesn't exist on
      // touch — a tap is the cursor); a tap near a sampled point ALSO selects it.
      patchCursor({ hoverCursor: { x, y } });
      const point: StudySelectedPoint | null = nearestSelectablePoint({ x, y }, clickCtxRef.current);
      if (point) patchCursor({ selectedPoint: point });
    },
    [patchCursor],
  );
  const handleHeroHover = useMemo(
    () => (p: { x: number; y: number } | null) => patchCursor({ hoverCursor: p }),
    [patchCursor],
  );
  const overlayFor = (data: ReturnType<typeof useStudySurface>["data"]): ReactNode => {
    const xBounds = data?.x_bounds ?? null;
    const yBounds = data?.y_bounds ?? null;
    return (
      <>
        <PointOverlay
          points={boardPoints}
          proposedBatch={whereNext}
          xRange={xBounds ?? undefined}
          yRange={yBounds ?? undefined}
        />
        <StudyCrosshair
          projectId={projectId}
          branch={branch}
          xBounds={xBounds}
          yBounds={yBounds}
          selected={selectedXY}
          margin={PLOTLY_PLOT_MARGIN}
        />
      </>
    );
  };
  const clOverlay = useMemo(
    () => overlayFor(clQuery.data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clQuery.data, boardPoints, whereNext, selectedXY, projectId, branch],
  );
  const cdOverlay = useMemo(
    () => overlayFor(cdQuery.data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cdQuery.data, boardPoints, whereNext, selectedXY, projectId, branch],
  );

  // Slice drawer — renders NOTHING until dimensionality demands it (>2 swept
  // axes), the SliceControls rule (§5.2 / acceptance 6).
  const [drawerOpen, setDrawerOpen] = useState(false);
  const showDrawer = rangedAxes.length > 2;
  const pinAxes = rangedAxes.filter((a) => a.name !== xInput && a.name !== yInput);

  // Promotion gestures: click focuses (1-up); drag onto the lanes splits.
  const [dragOver, setDragOver] = useState(false);
  const focusCard = (id: ExploreCardId) => onLanesChange([id]);
  const splitCard = (id: ExploreCardId) => onLanesChange(addLane(lanes, id));
  const demoteCard = (id: ExploreCardId) => onLanesChange(removeLane(lanes, id));
  const onLanesDragOver = (e: DragEvent) => {
    if (!e.dataTransfer.types.includes(CARD_DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    if (!dragOver) setDragOver(true);
  };
  const onLanesDrop = (e: DragEvent) => {
    const id = e.dataTransfer.getData(CARD_DRAG_MIME);
    setDragOver(false);
    if (!id) return;
    e.preventDefault();
    splitCard(id as ExploreCardId);
  };

  const seg = (
    options: ReadonlyArray<{ id: string; label: string }>,
    active: string,
    onPick: (id: string) => void,
    label: string,
  ) => (
    <div className="hlw-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} type="button" data-on={active === o.id ? "1" : "0"} onClick={() => onPick(o.id)}>
          {o.label}
        </button>
      ))}
    </div>
  );

  const mono10: CSSProperties = { fontFamily: MONO, fontSize: 10, color: palette.ink3 };

  // V5-tokened plot chrome for the hero lanes (§9.12 — the palette values are
  // already resolved from the --v5-* custom properties, both themes). The
  // adaptive-sampling mock views keep their own dark look (prop omitted there).
  const plotAppearance = useMemo(
    () => ({
      paperBg: palette.surface,
      plotBg: palette.surface,
      text: palette.ink2,
      muted: palette.ink3,
      grid: palette.line,
      hoverBg: palette.surface2,
      hoverBorder: palette.blue,
    }),
    [palette],
  );

  // ── lane content (each lane = one card, all sharing the cursor) ────────────
  const laneBody = (card: ExploreCardId): ReactNode => {
    if (card === "geometry") {
      return geometryHero ?? (
        <div className="hlw-xb-empty">the live section renders here</div>
      );
    }
    if (card === "cl" || card === "cd") {
      const view = viewByCard[card] ?? "2d";
      const output = card === "cd" ? "CD" : "CL";
      if (view === "3d") {
        return <StudyTrustSurface projectId={projectId} branch={branch} inputs={inputs} quantity={output} fill pollMs={pollMs} />;
      }
      const grid = card === "cd" ? cdGrid : clGrid;
      const data = card === "cd" ? cdQuery : clQuery;
      // Upstream honesty (review fix, CD flat-pink): a degenerate grid means
      // the sources publish NO variation for this quantity at this slice —
      // say so instead of rendering a misleading uniform wash + fake ridge.
      if (grid?.degenerate) {
        return (
          <div className="hlw-xb-empty" data-testid={`explore-degenerate-${card}`}>
            flat — sources publish no {output} variation at this slice
          </div>
        );
      }
      return grid ? (
        <HeatmapPanel
          x={grid.x}
          y={grid.y}
          z={grid.z}
          uncertainty={grid.sigma}
          xLabel={axisLabel(xInput)}
          yLabel={axisLabel(yInput)}
          zLabel={output}
          highlight="ridge"
          fillHeight
          onClick={handleHeroClick}
          onHover={handleHeroHover}
          plotAppearance={plotAppearance}
          overlay={card === "cd" ? cdOverlay : clOverlay}
        />
      ) : (
        <div className="hlw-xb-empty" data-testid="explore-hero-empty">
          {data.isLoading
            ? "fitting the response surface…"
            : data.isError
              ? "surface unavailable — solve a few points to seed the database"
              : "no solved points yet — solve a point or run a fill"}
        </div>
      );
    }
    if (card === "trust3d") {
      return <StudyTrustSurface projectId={projectId} branch={branch} inputs={inputs} quantity="CL" fill pollMs={pollMs} />;
    }
    if (card === "polar") {
      return polarSeries.length > 0 ? (
        <PolarPlot
          series={polarSeries}
          height={360}
          clMax={clMax}
          stallAlpha={stallAlpha}
          // Click a polar point → it becomes the shared selected point (the
          // geometry card re-rigs + the flow slice follows via runNodeId).
          onPick={(si, pi) => {
            const p = polarSeries[si]?.points[pi];
            if (!p) return;
            patchCursor({ selectedPoint: { runNodeId: p.nodeId ?? null, inputs: { alpha_deg: p.alpha } } });
          }}
        />
      ) : (
        <div className="hlw-xb-empty">no solved points with Cₗ yet</div>
      );
    }
    if (card === "cp") {
      return cpStrip ? (
        <div style={{ position: "relative", flex: 1, minHeight: 120 }}><CpSvg cpStrip={cpStrip} /></div>
      ) : (
        <div className="hlw-xb-empty">Cp appears after a solve emits surface pressure.</div>
      );
    }
    // campaign
    return campaign ? (
      <div className="hlw-xb-campaign" data-testid="explore-campaign-lane">
        {/* The ONE FillMap render site (review fix): the N-D "box building
            out" map now has a steady-state home — visible for a LIVE campaign,
            and during a replay the cursor drives it (upTo). */}
        {replaySteps.length > 0 ? (
          <div style={{ marginBottom: 10 }} data-testid="campaign-fillmap">
            <FillMap
              steps={replaySteps}
              upTo={replayCursor ?? null}
              axes={campaign.config?.axes ?? []}
              coverage={campaign.state?.coverage ?? null}
              total={replayCursor == null ? campaign.state?.dispatched ?? null : null}
              palette={palette}
              onPickStep={handlePickStep}
            />
          </div>
        ) : null}
        {/* The campaign's OWN solves as CL-vs-axis — every dot clicks through to
            its run node (FXC-9009 campaign → plots → provenance). */}
        {replaySteps.length > 0 ? (
          <div style={{ marginBottom: 10 }}>
            <CampaignPointsPlot
              steps={replaySteps}
              upTo={replayCursor ?? null}
              axes={campaign.config?.axes ?? []}
              palette={palette}
              onPickStep={handlePickStep}
            />
          </div>
        ) : null}
        <FillTailorSurface campaign={campaign} projectId={projectId} branch={branch} palette={palette} />
      </div>
    ) : (
      <div className="hlw-xb-empty">no fill campaign yet — configure one in the inspector (Axes)</div>
    );
  };

  const focus = lanes[0] ?? "cl";
  const focusOutput = focus === "cd" ? "CD" : "CL";

  // ── filmstrip cards ────────────────────────────────────────────────────────
  const coverage = useMemo(() => {
    const its = campaign?.state?.iterations ?? [];
    for (let i = its.length - 1; i >= 0; i -= 1) {
      const c = its[i]?.coverage_after;
      if (typeof c === "number" && Number.isFinite(c)) return c;
    }
    return null;
  }, [campaign]);

  const miniBounds = (grid: EvidenceGrid | null): { xB: [number, number] | null; yB: [number, number] | null } => ({
    xB: grid && grid.x.length ? [grid.x[0]!, grid.x[grid.x.length - 1]!] : null,
    yB: grid && grid.y.length ? [grid.y[0]!, grid.y[grid.y.length - 1]!] : null,
  });

  const cardBody = (id: ExploreCardId): ReactNode => {
    switch (id) {
      case "geometry": {
        const sel = cursor.selectedPoint;
        return (
          <>
            <GeometryThumb configuration={configuration} palette={palette} />
            <em>
              {sel
                ? `geometry 🔒 @ α ${Number(sel.inputs.alpha_deg ?? NaN).toFixed(1)}° · d ${Number(sel.inputs.flap_deploy_deg ?? NaN).toFixed(0)}°`
                : "geometry 🔒 · live"}
            </em>
          </>
        );
      }
      case "cl": {
        const { xB, yB } = miniBounds(clGrid);
        return (
          <>
            <MiniHeatmap grid={clGrid} />
            <StudyCrosshair projectId={projectId} branch={branch} xBounds={xB} yBounds={yB} selected={selectedXY} margin={MINI_INSET} />
            <em>CL {campaignRunning ? "●" : ""}</em>
          </>
        );
      }
      case "cd": {
        const { xB, yB } = miniBounds(cdGrid);
        return (
          <>
            {cdGrid?.degenerate ? null : <MiniHeatmap grid={cdGrid} />}
            <StudyCrosshair projectId={projectId} branch={branch} xBounds={xB} yBounds={yB} selected={selectedXY} margin={MINI_INSET} />
            <em>{cdGrid?.degenerate ? "CD · flat (no source variation)" : "CD"}</em>
          </>
        );
      }
      case "trust3d": {
        const { xB, yB } = miniBounds(confidenceGrid);
        return (
          <>
            <MiniHeatmap grid={confidenceGrid} />
            <StudyCrosshair projectId={projectId} branch={branch} xBounds={xB} yBounds={yB} selected={selectedXY} margin={MINI_INSET} />
            <em>3D trust</em>
          </>
        );
      }
      case "polar":
        return (
          <>
            <MiniPolar series={polarSeries} palette={palette} />
            <em>polar{clMax ? ` · CLmax ${clMax.CL.toFixed(2)}` : ""}</em>
          </>
        );
      case "cp":
        return (
          <>
            <CpSvg cpStrip={cpStrip} mini />
            <em>Cp</em>
          </>
        );
      case "campaign":
        return (
          <>
            <div style={{ ...mono10, padding: "6px 6px 0", lineHeight: 1.5 }}>
              {campaign
                ? `${campaign.state?.status ?? "?"} · ${campaign.state?.completed ?? 0}/${campaign.config?.budget ?? "?"} pts${coverage != null ? ` · cov ${(coverage * 100).toFixed(0)}%` : ""}`
                : "no campaign"}
            </div>
            <em>campaign{campaignRunning ? " ●" : ""}</em>
          </>
        );
    }
  };

  return (
    <div className="hlw-explore" data-testid="hlw-explore-board">
      {/* board header — the Explore room's single control row */}
      <div className="hlw-xb-head">
        <span className="hlw-xb-title" data-testid="explore-board-title">
          {lanes.length > 1
            ? `${lanes.length}-up · f(${axisLabel(xInput)}, ${axisLabel(yInput)})`
            : focus === "cl" || focus === "cd" || focus === "trust3d"
              ? `${focusOutput} = f(${axisLabel(xInput)}, ${axisLabel(yInput)})`
              : EXPLORE_CARDS.find((c) => c.id === focus)?.label ?? focus}
        </span>
        {lanes.length === 1 && (focus === "cl" || focus === "cd") ? (
          seg(
            [{ id: "2d", label: "2D" }, { id: "3d", label: "3D" }],
            viewByCard[focus] ?? "2d",
            (v) => setViewByCard((m) => ({ ...m, [focus]: v as "2d" | "3d" })),
            "Surface projection",
          )
        ) : null}
        {seg(
          [
            { id: "raw", label: "raw" },
            { id: "compatible", label: "compat" },
            { id: "corrected", label: "corrected" },
          ],
          cursor.evidenceMode,
          (m) => patchCursor({ evidenceMode: m as StudyEvidenceMode }),
          "Evidence mode",
        )}
        {headerExtra}
        <span style={{ flex: 1 }} />
        {showDrawer ? (
          <button
            type="button"
            className="hlw-cond"
            style={{ cursor: "pointer", borderColor: drawerOpen ? palette.primary : undefined, color: drawerOpen ? palette.primary : undefined }}
            onClick={() => setDrawerOpen((v) => !v)}
            data-testid="explore-slice-toggle"
            title="High-D slice: pick the 2 view axes, pin the rest"
          >
            slice · {pinAxes.length} held
          </button>
        ) : null}
        <button
          type="button"
          className="hlw-cond"
          style={{ cursor: "pointer" }}
          onClick={onOpenAdb}
          data-testid="explore-open-adb"
          title="Open the full aerodynamic database explorer — carries this cursor across"
        >
          open full ADB →
        </button>
      </div>

      {/* hero lanes — 1-up / 2-up / quad; a drop target for strip cards */}
      <div
        className="hlw-xb-lanes"
        data-testid="explore-hero-lane"
        data-count={lanes.length}
        data-dragover={dragOver ? "1" : "0"}
        onDragOver={onLanesDragOver}
        onDragLeave={() => setDragOver(false)}
        onDrop={onLanesDrop}
      >
        {lanes.map((card) => (
          <div key={card} className="hlw-xb-lane" data-testid={`explore-lane-${card}`}>
            {lanes.length > 1 ? (
              <div className="hlw-xb-lane-h">
                <span>{EXPLORE_CARDS.find((c) => c.id === card)?.label ?? card}</span>
                {card === "cl" || card === "cd" ? (
                  <span className="hlw-seg" role="group" aria-label={`${card} projection`}>
                    {(["2d", "3d"] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        data-on={(viewByCard[card] ?? "2d") === v ? "1" : "0"}
                        onClick={() => setViewByCard((m) => ({ ...m, [card]: v }))}
                      >
                        {v.toUpperCase()}
                      </button>
                    ))}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="hlw-xb-lane-x"
                  title="Demote this panel back to the filmstrip"
                  aria-label={`Demote ${card}`}
                  data-testid={`explore-lane-demote-${card}`}
                  onClick={() => demoteCard(card)}
                >
                  ×
                </button>
              </div>
            ) : null}
            <div className="hlw-xb-lane-b">{laneBody(card)}</div>
          </div>
        ))}
      </div>

      {/* slice drawer (only when >2 swept axes — the highD ladder, §5.2) */}
      {showDrawer && drawerOpen ? (
        <div className="hlw-xb-drawer" data-testid="explore-slice-drawer">
          <div className="hlw-xb-drawer-row">
            {(["x", "y"] as const).map((slot) => (
              <label key={slot} style={mono10}>
                {slot.toUpperCase()}
                <select
                  value={slot === "x" ? xInput : yInput}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (slot === "x") {
                      patchCursor(v === yInput ? { xInput: v, yInput: xInput } : { xInput: v });
                    } else {
                      patchCursor(v === xInput ? { yInput: v, xInput: yInput } : { yInput: v });
                    }
                  }}
                >
                  {rangedAxes.map((a) => (
                    <option key={a.name} value={a.name}>{axisLabel(a.name)}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          {pinAxes.map((a) => {
            const lo = a.min ?? 0;
            const hi = a.max ?? 1;
            const stored = Math.min(hi, Math.max(lo, cursor.pinned[a.name] ?? 0.5 * (lo + hi)));
            return (
              <PinSlider
                key={a.name}
                label={axisLabel(a.name)}
                lo={lo}
                hi={hi}
                value={stored}
                mono={mono10}
                onCommit={(v) => patchCursor({ pinned: { [a.name]: v } })}
              />
            );
          })}
          <PinSlider
            label="slab ±%"
            lo={0}
            hi={0.5}
            value={cursor.slabFraction}
            mono={mono10}
            fmt={(v) => `${Math.round(v * 100)}%`}
            onCommit={(v) => patchCursor({ slabFraction: v })}
          />
        </div>
      ) : null}

      {/* story overlay band (autoplay ticker) — its OWN reserved row just above
          the strip, never overlapping the thumbnails it narrates (review fix). */}
      {overlay}

      {/* the LLM "why" terminal — its OWN collapsible strip (collapsed by
          default, calm-first). The richer sibling of the ticker (FXC-9009). */}
      {whyTerminal}

      {/* filmstrip — slot 0 geometry, locked; click promotes, drag splits */}
      <div className="hlw-xb-strip" data-testid="explore-filmstrip" role="tablist" aria-label="Explore panels" data-pulse={pulsing ? "1" : "0"}>
        {EXPLORE_CARDS.map((card) => (
          <button
            key={card.id}
            type="button"
            role="tab"
            aria-selected={lanes.includes(card.id)}
            className={`hlw-xb-card${card.locked ? " lock" : ""}`}
            data-on={lanes.includes(card.id) ? "1" : "0"}
            data-testid={`strip-card-${card.id}`}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(CARD_DRAG_MIME, card.id);
              e.dataTransfer.effectAllowed = "copy";
            }}
            onClick={() => focusCard(card.id)}
            title={
              card.locked
                ? "Geometry — locked to slot 0; click to promote, drag onto the hero to split"
                : `${card.label} — click to promote, drag onto the hero to split (2-up, quad)`
            }
          >
            {cardBody(card.id)}
          </button>
        ))}
      </div>
    </div>
  );
}
